class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.init()

    // Add resize observer to update container height
    const resizeObserver = new ResizeObserver(entries => this.update());
    resizeObserver.observe(this);

    // Bind event listeners
    this.navItems.forEach(item => item.addEventListener('click', this.onNavItemClick.bind(this)))
  }

  init() {
    // Set up our DOM element variables
    this.imagesContainer = this.querySelector('.product-gallery__images');
    this.navItems = this.querySelectorAll('.product-gallery__nav-item');
    this.images = this.querySelectorAll('.product-gallery__image');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.upBtn = this.querySelector('.vertical-thumbnails-up');
    this.downBtn = this.querySelector('.vertical-thumbnails-down');
    this.thumbnailList = this.querySelector('.product-gallery__nav-list, [id^="VerticalThumbnailList-"]');
    // If there is no active images set the first image to active
    if (this.findCurrentIndex() === -1) {
      this.setCurrentImage(this.images[0])
    }
    this.initArrowNavigation();
  }

  /**
   * Initializes up/down arrow navigation for vertical thumbnails.
   */
  initArrowNavigation() {
    if (!this.thumbnailList) return;
    this.updateArrows = this.updateArrows.bind(this);
    if (this.upBtn) {
      this.upBtn.addEventListener('click', this.onUpClick.bind(this));
    }
    if (this.downBtn) {
      this.downBtn.addEventListener('click', this.onDownClick.bind(this));
    }
    this.thumbnailList.addEventListener('scroll', this.updateArrows);
    setTimeout(this.updateArrows, 100);
  }

  /**
   * Updates the enabled/disabled state of up/down arrow buttons based on scroll position.
   */
  updateArrows() {
    if (!this.thumbnailList) return;
    if (this.upBtn) this.upBtn.disabled = this.thumbnailList.scrollTop === 0;
    if (this.downBtn) this.downBtn.disabled = this.thumbnailList.scrollTop + this.thumbnailList.clientHeight >= this.thumbnailList.scrollHeight - 1;
  }

  /**
   * Sets the active thumbnail and triggers the main image update.
   * @param {Element} newActive - The new active thumbnail element.
   */
  setActiveThumb(newActive) {
    if (!this.thumbnailList) return;
    const allThumbs = this.thumbnailList.querySelectorAll('.product-gallery__nav-item');
    allThumbs.forEach(thumb => thumb.classList.remove('product-gallery__nav-item--active'));
    newActive.classList.add('product-gallery__nav-item--active');
    newActive.dispatchEvent(new Event('click', {bubbles: true}));
  }

  /**
   * Handles click on the up arrow button.
   */
  onUpClick() {
    if (!this.thumbnailList) return;
    const thumbs = Array.from(this.thumbnailList.querySelectorAll('.product-gallery__nav-item'));
    const current = thumbs.findIndex(thumb => thumb.classList.contains('product-gallery__nav-item--active'));
    if (current > 0) {
      this.setActiveThumb(thumbs[current - 1]);
      thumbs[current - 1].scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }
  }

  /**
   * Handles click on the down arrow button.
   */
  onDownClick() {
    if (!this.thumbnailList) return;
    const thumbs = Array.from(this.thumbnailList.querySelectorAll('.product-gallery__nav-item'));
    const current = thumbs.findIndex(thumb => thumb.classList.contains('product-gallery__nav-item--active'));
    if (current < thumbs.length - 1) {
      this.setActiveThumb(thumbs[current + 1]);
      thumbs[current + 1].scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }
  }

  setActiveMedia(mediaId) {
    const currentImage = Array.from(this.images).find(item => item.dataset.mediaId == mediaId);
    if (currentImage) {
      this.images.forEach(item => item.classList.remove('product-gallery__image--active'));
      currentImage.classList.add('product-gallery__image--active');
    }

    const currentThumb = Array.from(this.navItems).find(item => item.dataset.mediaId == mediaId);
    if (currentThumb) {
      this.navItems.forEach(item => item.classList.remove('product-gallery__nav-item--active'));
      currentThumb.classList.add('product-gallery__nav-item--active');
      currentThumb.scrollIntoView({
        behavior: 'auto',
        block: 'nearest'
      });
    }
  }

  onNavItemClick(event) {
    const clickedItem = event.target.closest('li')
    const mediaId = clickedItem.dataset.mediaId

    this.navItems.forEach(item => item.classList.remove('product-gallery__nav-item--active'))
    clickedItem.classList.add('product-gallery__nav-item--active')

    this.images.forEach(item => item.classList.remove('product-gallery__image--active'))
    const targetImage = Array.from(this.images).find(item => item.dataset.mediaId === mediaId)
    if (targetImage) {
      targetImage.classList.add('product-gallery__image--active')
    }

    this.update()
  }

  update() {
    const thumbnailWrapper = this.querySelector('.thumbnail-wrapper');
    // Scroll active thumbnail into view
    const activeThumb = this.querySelector('.product-gallery__nav-item--active');
    if (activeThumb) {
      activeThumb.scrollIntoView({
        behavior: 'instant', // or 'auto'
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }

  setCurrentImage(elem) {
    this.images.forEach(item => item.classList.remove('product-gallery__image--active'))
    elem.classList.add('product-gallery__image--active')
    this.update()
  }

  findCurrentIndex() {
    return Array.from(this.images).findIndex(item => item.classList.contains('product-gallery__image--active'))
  }

  updateMediaContent(newHtml) {
    const newGallery = newHtml.querySelector('.product-gallery__images');
    if (!newGallery) return;

    const newItems = newGallery.querySelectorAll('.product-gallery__image');
    const currentItems = this.querySelectorAll('.product-gallery__image');

    newItems.forEach((newItem) => {
      const mediaId = newItem.dataset.mediaId;
      const currentItem = Array.from(currentItems).find(
        (item) => item.dataset.mediaId === mediaId
      );
      if (currentItem) {
        const newContent = newItem.innerHTML.trim();
        const currentContent = currentItem.innerHTML.trim();
        if (newContent !== currentContent) {
          currentItem.innerHTML = newContent;
        }
      }
    });

    // Update references
    this.images = this.querySelectorAll('.product-gallery__image');
    this.update();
  }

  onButtonClick(event) {
    event.preventDefault();
    let index = this.findCurrentIndex()
    if (event.currentTarget.name === 'next') {
      index++
    } else {
      index--
    }
    this.setCurrentImage(this.images[index])
  }
}

customElements.define('product-gallery', ProductGallery);

// --- Mobile Swiper sync on variant change ---
document.addEventListener('DOMContentLoaded', function() {
  var mobileSwiperEl = document.querySelector('.mobile-vertical-gallery .product-gallery-swiper');
  if (!mobileSwiperEl || typeof Swiper === 'undefined' || typeof subscribe !== 'function') return;

  var swiper = new Swiper(mobileSwiperEl, {
    loop: false,
    pagination: {
      el: mobileSwiperEl.querySelector('.swiper-pagination'),
      clickable: true,
    },
    slidesPerView: 1,
    spaceBetween: 0,
  });

  // Use Shopify's pub/sub system for variant changes
  subscribe('variant-change', function(event) {
    var variant = event.data && event.data.variant;
    if (!variant || !variant.featured_media || !variant.featured_media.id) return;
    var mediaId = variant.featured_media.id.toString();
    var slides = mobileSwiperEl.querySelectorAll('.swiper-slide');
    var targetIndex = Array.from(slides).findIndex(function(slide) {
      var mediaElem = slide.querySelector('[data-media-id]');
      return mediaElem && mediaElem.getAttribute('data-media-id') == mediaId;
    });
    if (targetIndex !== -1) {
      swiper.slideTo(targetIndex);
    }
  });
});
